import { HeroBackground, ProductMain } from '@prishapolicy/shared/ui';
import {
  ChoosePlan,
  ChoosePlanProps,
  countriesData,
  travelfaqList,
  useW3PurchaseFlow,
  ProductCategoryPageProps,
  getPurchaseFlowHeroForms,
  // protectCardCheck,
  productsPathAdder,
  HeroTicker,
  ProtectToday,
  travelCarouselItems,
  VerticalCarousel,
  WhyChooseUs,
  Faq,
} from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import Image, { StaticImageData } from 'next/image';
import CheckFillIcon from 'remixicon-react/CheckFillIcon';
import americas from './americas.png';
import travelClock from './travel-clock.png';
const productCategorySlug = 'travel-insurance';

type TravelGlobeCardProps = {
  name: string;
  places: string[];
  image: StaticImageData;
};
const TravelGlobeCard = (props: TravelGlobeCardProps) => {
  return (
    <div className="rounded-huge border-grey-200 border bg-white pt-7 pl-7">
      <h5 className="heading-5 mb-3">{props.name}</h5>
      <div className="grid grid-cols-3">
        <div className="col-span-2 grid grid-cols-2 ">
          {props.places.map((place) => {
            return (
              <div key={place} className="mb-2 flex">
                <CheckFillIcon className="text-green-500" />
                <span className="ml-2 text-gray-500">{place}</span>
              </div>
            );
          })}
        </div>
        <div style={{}}>
          <Image src={americas} alt={props.name} />
        </div>
      </div>
    </div>
  );
};
const ListOfCountries = () => {
  return (
    <div className="container py-20">
      <div className="mx-auto w-3/4 text-center">
        <h2 className="heading-2">List of Countries where Travel Insurance is mandatory</h2>
        <p>
          Most of the countries post pandemic now requires a travel visa. Here are some of the countries that require
          mandatory overseas travel insurance, irrespective of how you are travelling.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4">
        {countriesData.map((countryData) => {
          return <TravelGlobeCard key={countryData.name} {...countryData} />;
        })}
      </div>
      <p className="mx-auto mt-8 w-3/5 italic">
        This is an indicative list, The information contained serves as a general overview and should only be used for
        informational purposes. It is advisable to check each country&apos;s visa requirement independently before
        travel.
      </p>
    </div>
  );
};
export function TravelInsurance(props: ProductCategoryPageProps) {
  const choosePlanCards: ChoosePlanProps['cards'] = [
    {
      title: 'Cancellation Coverage',
      content: (
        <div>
          Without the right cancellation cover, you may have to forfeit your trip without being compensated for your
          ticket or other bookings.
          <br />
          Always choose a policy that is flexible and provides maximum returns on cancellation.
        </div>
      ),
    },
    {
      title: 'Baggage Losses & Delays',
      content: (
        <div>
          Losing travel luggage can cut a trip short and often leave you stranded in a foreign place without any
          support.
          <br />
          Choose a travel policy that guarantees the return of baggage or its cost, provides detailed tracking of the
          items and also covers delays in transit.
        </div>
      ),
    },
    {
      title: 'Personal Liability',
      content: (
        <div>
          In case of accidents that involve harm to another person&apos;s life or property, you will have to incur heavy
          costs that can be life-changing.
          <br />
          Go for plans that insure against any damages to third-party assets and cover the maximum chunk of the bill
          that is incurred.
        </div>
      ),
    },
    {
      title: 'Medical Expenses',
      content: (
        <div>
          The bills of treatment abroad can amount to thousands of Dollars/Euros. Therefore, it is best to choose a
          policy that covers most of the bill.
          <br />
          If the policy providers have specific hospital coverage in the location you are travelling, it is the best
          option.
        </div>
      ),
    },
    {
      title: 'Travel Assistance',
      content: (
        <div>
          IDV is the claim amount that the insurer will pay in the event of an accident or when it requires a repair
          covered by the policy.
          <br />
          Based on your vehicle, ensure to forecast expenses and calculate how much IDV you need.
        </div>
      ),
    },
    {
      title: 'Easy of Passport Retrieval',
      content: (
        <div>
          Additional covers include assistance with processes and services other than monetary payments.
          <br />
          They may be roadside assistance or zero-depreciation covers, the types of services depends on the insurers you
          choose.
        </div>
      ),
    },
  ];
  const { IconRenderer, FormRenderer } = useW3PurchaseFlow(props);
  return (
    <>
      <NextSeo
        title="Travel Insurance | PrishaPolicy"
        description="Travel Insurance : Travel with peace of mind, knowing that our comprehensive insurance has you covered for unexpected travel mishaps and disruptions."
      />
      <ProductMain color="blue">
        <HeroBackground backgroundColor="blue-300" PurchaseFlowFormRenderer={FormRenderer}>
          <div className="grid-cols-2 lg:grid ">
            <div className="flex flex-col justify-between">
              <div>
                <h1 className="heading-1 pb-3">
                  <span className="font-normal">Getting in or flying out?</span>
                  <br />
                  Travel the world,
                  <br />
                  carefree!
                </h1>
                <p className="text-title-6 max-w-[600px]">
                  Travel with peace of mind, knowing that our comprehensive insurance has you covered for unexpected
                  travel mishaps and disruptions.
                </p>
              </div>
              <IconRenderer />
            </div>
            <div className="object-contain">
              <video src="/products/travel-insurance/animation.webm" autoPlay muted playsInline loop></video>
            </div>
          </div>
        </HeroBackground>
        <HeroTicker />
        <ProtectToday productCategoryPrefix={productCategorySlug} items={props.productPaths} maxPerRow={4} />
        <div className="container flex grid-cols-2 flex-col-reverse gap-20 py-24 lg:grid">
          <div className="grid place-items-center ">
            <Image src={travelClock} alt="" />
          </div>
          <div className="flex flex-col justify-center">
            <h3 className="lg:heading-3 heading-4">Travel Insurance — what is it?</h3>
            <div className="prose mt-6">
              In a nutshell, travel insurance gives you security and a safety net from any unexpected mishaps that may
              happen during your trip.
              <br />
              General incidents covered under travel insurance include:
              <ul>
                <li>Loss or delay of luggage</li>
                <li>Trip cancellations or curtailment</li>
                <li>Damage to personal property</li>
                <li>Loss of passport in a foreign land</li>
              </ul>
              Some policies also give you and your family coverage for medical expenses, accidental demise, or accidents
              during the trip.
            </div>
          </div>
        </div>
        <div className="container">
          <div className="w-3/4">
            <h2 className="heading-2">The Necessity of Travel Insurance</h2>
            <p className="mt-4">
              Travel Plans are varied and multi-faceted. And different plans offer different perks when subscribing to
              it. To choose the perfect plan for your trip, here are a few factors you should look at while choosing a
              policy.
            </p>
          </div>
        </div>

        <div className="mt-20 mb-16">
          <div className=" container lg:px-4">
            <div>
              <VerticalCarousel carouselItems={travelCarouselItems} />
            </div>
          </div>
        </div>

        <ChoosePlan cards={choosePlanCards} />
        <ListOfCountries />
        <WhyChooseUs />
        <Faq list={travelfaqList} />
      </ProductMain>
    </>
  );
}

export function getStaticProps() {
  // protectCardCheck(productCategorySlug, productsPathAdder(productCategorySlug));
  const productPaths = productsPathAdder(productCategorySlug);
  return {
    props: {
      ...getPurchaseFlowHeroForms(productCategorySlug).props,
      productPaths,
    },
  };
}
export default TravelInsurance;
